import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { Command as CommandPrimitive } from "cmdk";
import { ChevronDown, ChevronRight, Menu, Search, Sparkles, Gauge, Workflow, Book, Building2, FileText, RefreshCw, History, BadgeDollarSign, Crosshair, ClipboardCheck, Handshake, BarChart3, TrendingUp, Scale, Briefcase, FolderOpen } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { Separator } from "../components/ui/separator";
import { toast } from "../hooks/use-toast";
import { seedIfEmpty, reseedAll, getDeals, getPortfolio, getLPs, getActivities, getAuditLog } from "../mock";

export function useSeed() {
  useEffect(() => { seedIfEmpty(); }, []);
}

export function Shell({ children }) {
  useSeed();
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Montserrat', ui-sans-serif, system-ui" }}>
      <Topbar />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-6 lg:p-8 space-y-6 bg-[#F7F9FC]">{children}</main>
      </div>
    </div>
  );
}

export function Topbar() {
  const [open, setOpen] = useState(false);
  const [team, setTeam] = useState(localStorage.getItem("atlas_team") || "PE Investment");
  useEffect(() => {
    const down = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const nav = useNavigate();

  return (
    <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
      <div className="flex items-center gap-3 px-4 lg:px-6 py-3">
        {/* GII Logo */}
        <NavLink to="/" className="flex items-center gap-3 shrink-0">
          <img src="/gii-logo.svg" alt="GII" className="h-9 w-auto" />
        </NavLink>
        {/* Top Nav Links */}
        <div className="hidden md:flex items-center gap-1 ml-4">
          <NavItem to="/opportunities" icon={<Crosshair size={15} />} label="Opportunities" />
          <NavItem to="/due-diligence" icon={<ClipboardCheck size={15} />} label="Due Diligence" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative hidden md:block">
            <Input
              placeholder="Search… (⌘K)"
              className="pl-8 bg-gray-50 border-gray-200 text-[#383838] placeholder:text-[#616161] focus:border-[#0078C1] focus:ring-[#0078C1] h-9 text-sm"
              onFocus={() => setOpen(true)}
              readOnly
            />
            <Search size={14} className="absolute left-2.5 top-2.5 text-[#616161]" />
          </div>
          {/* Team Switcher */}
          <div className="flex items-center text-xs text-[#616161] gap-2">
            <span className="hidden sm:inline font-medium text-[#383838]">Team:</span>
            <TeamSwitcher team={team} onChange={(t) => {
              setTeam(t);
              localStorage.setItem("atlas_team", t);
              window.dispatchEvent(new Event("atlas_team_changed"));
              toast({ title: `Switched to ${t}` });
            }} />
          </div>
          <DemoControls />
        </div>
      </div>
      <CommandPalette open={open} onOpenChange={setOpen} onNavigate={(to) => nav(to)} />
    </div>
  );
}

function NavItem({ to, icon, label }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md font-medium transition-colors ${
          isActive
            ? "bg-[#E8F4FD] text-[#0078C1]"
            : "text-[#616161] hover:bg-gray-100 hover:text-[#383838]"
        }`
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}

function TeamSwitcher({ team, onChange }) {
  const teams = ["PE Investment", "VC Investment", "Hybrid"];
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-gray-200 text-[#383838] hover:bg-gray-50 h-8 px-3 text-sm font-medium"
        >
          {team} <ChevronDown size={14} className="ml-1 text-[#616161]" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white text-[#383838] border-gray-200 max-w-sm">
        <DialogHeader><DialogTitle className="text-[#383838] font-semibold">Switch Team</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3 pt-2">
          {teams.map(t => (
            <Button
              key={t}
              onClick={() => { onChange(t); setOpen(false); }}
              className={`h-10 font-medium text-sm ${team===t ? "bg-[#0078C1] text-white hover:bg-[#005A91]" : "bg-gray-100 text-[#383838] hover:bg-gray-200"}`}
            >
              {t}
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DemoControls() {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="outline"
      className="h-8 border-gray-200 text-[#616161] hover:bg-gray-50 hover:text-[#383838] text-sm"
      onClick={async () => {
        setBusy(true);
        reseedAll();
        await new Promise(r => setTimeout(r, 200));
        toast({ title: "Demo data reseeded" });
        setBusy(false);
        window.location.reload();
      }}
    >
      <RefreshCw size={14} className="mr-1" /> {busy ? "Reseeding…" : "Reset Data"}
    </Button>
  );
}

export function Sidebar() {
  const nav = useNavigate();
  const location = useLocation();
  const [workflowOpen, setWorkflowOpen] = useState(true);

  // GII-relevant sector pages that live under the Deal Workflow dropdown
  const workflowItems = [
    { to: "/opportunities", label: "Opportunities", icon: <Crosshair size={16} /> },
    { to: "/due-diligence", label: "Due Diligence", icon: <ClipboardCheck size={16} /> },
  ];

  const isWorkflowActive = workflowItems.some(it => location.pathname === it.to);

  const topItems = [
    { to: "/", label: "Overview", icon: <Gauge size={16} /> },
    { to: "/pipeline", label: "Pipeline CRM", icon: <Workflow size={16} /> },
    { to: "/knowledge", label: "Knowledge", icon: <Book size={16} /> },
    { to: "/portfolio", label: "Portfolio", icon: <Building2 size={16} /> },
  ];

  const SideItem = ({ to, label, icon, indent = false }) => {
    const isActive = location.pathname === to;
    return (
      <button
        onClick={() => nav(to)}
        className={`w-full flex items-center gap-2.5 text-left text-sm px-3 py-2 rounded-md font-medium transition-colors ${
          indent ? "pl-8" : ""
        } ${
          isActive
            ? "bg-[#E8F4FD] text-[#0078C1]"
            : "text-[#616161] hover:bg-gray-50 hover:text-[#383838]"
        }`}
      >
        <span className={isActive ? "text-[#0078C1]" : "text-[#616161]"}>{icon}</span>
        <span>{label}</span>
      </button>
    );
  };

  return (
    <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-gray-200 bg-white min-h-[calc(100vh-57px)]">
      <nav className="p-3 space-y-0.5">
        {topItems.map(it => (
          <SideItem key={it.to} {...it} />
        ))}

        <Separator className="my-2 bg-gray-100" />

        {/* Deal Workflow collapsible group */}
        <button
          onClick={() => setWorkflowOpen(o => !o)}
          className={`w-full flex items-center gap-2.5 text-left text-sm px-3 py-2 rounded-md font-semibold transition-colors ${
            isWorkflowActive
              ? "text-[#0078C1]"
              : "text-[#383838] hover:bg-gray-50"
          }`}
        >
          <FolderOpen size={16} className={isWorkflowActive ? "text-[#0078C1]" : "text-[#616161]"} />
          <span className="flex-1">Deal Workflow</span>
          {workflowOpen
            ? <ChevronDown size={14} className="text-[#616161]" />
            : <ChevronRight size={14} className="text-[#616161]" />
          }
        </button>
        {workflowOpen && (
          <div className="space-y-0.5">
            {workflowItems.map(it => (
              <SideItem key={it.to} {...it} indent />
            ))}
          </div>
        )}

        <Separator className="my-2 bg-gray-100" />
        <AuditViewer />
      </nav>
    </aside>
  );
}

function CommandPalette({ open, onOpenChange, onNavigate }) {
  const [teamKey, setTeamKey] = useState(0);
  useEffect(() => {
    const handler = () => setTeamKey(k => k + 1);
    window.addEventListener("atlas_team_changed", handler);
    return () => window.removeEventListener("atlas_team_changed", handler);
  }, []);
  
  const deals = useMemo(() => getDeals(), [teamKey]);
  const portfolio = useMemo(() => getPortfolio(), [teamKey]);
  const lps = getLPs();
  const navItems = [
    ["Overview", "/"],
    ["Identification", "/identification"],
    ["Underwriting", "/closure"],
    ["Valuation", "/diligence"],
    ["Execution", "/execution"],
    ["Deal Origination", "/origination"],
    ["Pipeline CRM", "/pipeline"],
    ["Knowledge", "/knowledge"],
    ["Portfolio", "/portfolio"],
    ["Portfolio Ops", "/portfolio-ops"],
    ["Competition", "/competition"],
  ];
  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search…" />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>
        <CommandGroup heading="Navigate">
          {navItems.map(([label, to]) => (
            <CommandItem key={to} onSelect={() => { onNavigate(to); onOpenChange(false); }}>{label}</CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Deals">
          {deals.slice(0, 30).map(d => (
            <CommandItem key={d.id} onSelect={() => { onNavigate(`/pipeline?focus=${d.id}`); onOpenChange(false); }}>
              <span className="mr-2 text-xs text-[#616161]">{d.id}</span> {d.name}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Portfolio">
          {portfolio.map(p => (
            <CommandItem key={p.id} onSelect={() => { onNavigate(`/portfolio?focus=${p.id}`); onOpenChange(false); }}>{p.name}</CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="LPs">
          {lps.map(l => (
            <CommandItem key={l.id} onSelect={() => { onNavigate(`/lp?focus=${l.id}`); onOpenChange(false); }}>{l.name}</CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

function AuditViewer() {
  const [open, setOpen] = useState(false);
  const log = getAuditLog();
  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        className="w-full h-9 border-gray-200 text-[#616161] hover:bg-gray-50 hover:text-[#383838] text-sm font-medium justify-start gap-2"
      >
        <History size={15} /> Audit Log
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white text-[#383838] border-gray-200 max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#383838]">Audit Log</DialogTitle></DialogHeader>
          {log.length === 0 && <div className="text-[#616161] text-sm py-4">No audit entries yet.</div>}
          <div className="space-y-2">
            {log.slice(0, 50).map(e => (
              <div key={e.id} className="text-xs border border-gray-100 rounded p-2 bg-gray-50">
                <span className="font-medium text-[#383838]">{e.action}</span>
                <span className="text-[#616161] ml-2">{e.entityType} {e.entityId}</span>
                <span className="text-[#616161] ml-2">{new Date(e.ts).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Shared UI Primitives ─────────────────────────────────────────────────────

export function Section({ title, children, action }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-[#616161]">{title}</h2>
        {action && <div>{action}</div>}
      </div>
      {children}
    </div>
  );
}

export function StatCard({ title, value, sub }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <div className="text-xs font-medium text-[#616161] uppercase tracking-wide mb-1">{title}</div>
      <div className="text-2xl font-bold text-[#383838]">{value}</div>
      {sub && <div className="text-xs text-[#616161] mt-1 truncate">{sub}</div>}
    </div>
  );
}
